/* eslint-disable */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  onAuthStateChanged,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
} from "firebase/auth";

import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";


import {
  clearStoredAdminSessionId,
  createAdminSession,
  getAdminSession,
  getStoredAdminSessionId,
  updateAdminSessionHeartbeat,
} from "../utils/createAdminSession";

import {
  clearSensitiveActionVerification,
} from "../utils/sensitiveActionAuth";

const AuthContext = createContext(null);

export const PORTAL_NAME = "School Portal";

const MFA_IDLE_LIMIT_MS = 30 * 60 * 1000;
const MFA_ABSOLUTE_LIMIT_MS = 12 * 60 * 60 * 1000;

function normaliseRole(input) {
  const role = String(input || "").toLowerCase().trim();
  if (role === "teacher") return "staff";
  if (role === "manager") return "admin";
  return role || null;
}

function readStoredMfaSession() {
  try {
    return {
      verified: localStorage.getItem("pp:mfaVerified") === "true",
      verifiedAt: Number(localStorage.getItem("pp:mfaVerifiedAt")) || null,
      lastActivityAt:
        Number(localStorage.getItem("pp:mfaLastActivityAt")) || Date.now(),
    };
  } catch {
    return { verified: false, verifiedAt: null, lastActivityAt: Date.now() };
  }
}

function storeMfaSession(now) {
  localStorage.setItem("pp:mfaVerified", "true");
  localStorage.setItem("pp:mfaVerifiedAt", String(now));
  localStorage.setItem("pp:mfaLastActivityAt", String(now));
}

function clearMfaSessionStorage() {
  localStorage.removeItem("pp:mfaVerified");
  localStorage.removeItem("pp:mfaVerifiedAt");
  localStorage.removeItem("pp:mfaLastActivityAt");
}

async function readClaims(user) {
  try {
    const tokenResult = await user.getIdTokenResult(true);
    return tokenResult?.claims || {};
  } catch (error) {
    console.error("Failed to read auth claims:", error);
    return {};
  }
}

async function readUserRoot(uid) {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    return snap.exists() ? snap.data() : null;
  } catch (error) {
    console.error("Failed to read /users profile:", error);
    return null;
  }
}

async function readStaffProfile(schoolId, uid) {
  if (!schoolId || !uid) return null;

  try {
    const snap = await getDoc(doc(db, "schools", schoolId, "staff", uid));
    return snap.exists() ? snap.data() : null;
  } catch (error) {
    console.error("Failed to read staff profile:", error);
    return null;
  }
}

async function readParentProfile(schoolId, parentDocId = "") {
  if (!schoolId || !parentDocId) return null;

  try {
    const snap = await getDoc(
      doc(db, "schools", schoolId, "parents", parentDocId)
    );

    return snap.exists()
      ? { id: snap.id, parentDocId: snap.id, ...snap.data() }
      : null;
  } catch (error) {
    console.error("Failed to read parent profile:", error);
    return null;
  }
}

function buildSafeProfile(firebaseUser, nextClaims = {}, source = {}) {
  const claimRole = normaliseRole(nextClaims?.role);
  const sourceRole = normaliseRole(source?.role);
  const resolvedRole = sourceRole || claimRole || null;

  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email || source?.email || "",
    displayName:
      source?.displayName || source?.name || firebaseUser.displayName || "",
    role: resolvedRole,
    schoolId:
      source?.schoolId ||
      nextClaims?.schoolId ||
      nextClaims?.sid ||
      nextClaims?.homeId ||
      null,
    ...(source || {}),
    roleResolved: resolvedRole,
  };
}

export function AuthProvider({ children }) {
  const storedMfa = readStoredMfaSession();

  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState(null);
  const [profile, setProfile] = useState(null);

  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  const [authResolved, setAuthResolved] = useState(false);
  const [profileResolved, setProfileResolved] = useState(false);

  const [mfaSessionVerified, setMfaSessionVerified] = useState(
    storedMfa.verified
  );
  const [mfaVerifiedAt, setMfaVerifiedAt] = useState(storedMfa.verifiedAt);
  const [mfaLastActivityAt, setMfaLastActivityAt] = useState(
    storedMfa.lastActivityAt
  );

  const lastProfileKeyRef = useRef("");

  const role =
    normaliseRole(profile?.role) || normaliseRole(claims?.role) || null;

  const schoolId =
    profile?.schoolId ||
    claims?.schoolId ||
    claims?.sid ||
    claims?.homeId ||
    null;

  const clearAuthState = useCallback(() => {
    setUser(null);
    setClaims(null);
    setProfile(null);

    setLoading(false);
    setProfileLoading(false);
    setAuthResolved(true);
    setProfileResolved(true);

    clearMfaSessionStorage();
    clearStoredAdminSessionId();

    setMfaSessionVerified(false);
    setMfaVerifiedAt(null);
    setMfaLastActivityAt(Date.now());

    lastProfileKeyRef.current = "";
  }, []);

  const resolveProfile = useCallback(async (firebaseUser, nextClaims = {}) => {
    if (!firebaseUser?.uid) {
      setProfile(null);
      setProfileLoading(false);
      setProfileResolved(true);
      return;
    }

    const roleFromClaims = normaliseRole(nextClaims?.role);

    const schoolIdFromClaims =
      nextClaims?.schoolId || nextClaims?.sid || nextClaims?.homeId || null;

    const profileKey = `${firebaseUser.uid}__${
      roleFromClaims || "none"
    }__${schoolIdFromClaims || "none"}`;

    if (lastProfileKeyRef.current === profileKey) {
      setProfileResolved(true);
      setProfileLoading(false);
      return;
    }

    lastProfileKeyRef.current = profileKey;

    setProfileLoading(true);
    setProfileResolved(false);

    try {
      const rootUser = await readUserRoot(firebaseUser.uid);

      const roleFromRoot = normaliseRole(rootUser?.role);
      const effectiveRole = roleFromRoot || roleFromClaims || null;
      const effectiveSchoolId = rootUser?.schoolId || schoolIdFromClaims || null;

      let resolvedProfile = null;

      if (effectiveRole === "super_admin") {
        resolvedProfile = buildSafeProfile(firebaseUser, nextClaims, {
          ...(rootUser || {}),
          role: "super_admin",
          schoolId: null,
        });
      } else if (effectiveRole === "admin") {
        resolvedProfile = buildSafeProfile(firebaseUser, nextClaims, {
          ...(rootUser || {}),
          role: "admin",
          schoolId: effectiveSchoolId,
        });
      } else if (effectiveRole === "staff") {
        let staffDoc = await readStaffProfile(
          effectiveSchoolId,
          firebaseUser.uid
        );

        if (!staffDoc) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          staffDoc = await readStaffProfile(effectiveSchoolId, firebaseUser.uid);
        }

        resolvedProfile = buildSafeProfile(firebaseUser, nextClaims, {
          ...(rootUser || {}),
          ...(staffDoc || {}),
          role: normaliseRole(
            staffDoc?.role || rootUser?.role || roleFromClaims
          ),
          schoolId: staffDoc?.schoolId || rootUser?.schoolId || effectiveSchoolId,
        });
      } else if (effectiveRole === "parent") {
        let parentDoc = await readParentProfile(
          effectiveSchoolId,
          rootUser?.parentDocId
        );

        if (!parentDoc) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          parentDoc = await readParentProfile(
            effectiveSchoolId,
            rootUser?.parentDocId
          );
        }

        resolvedProfile = buildSafeProfile(firebaseUser, nextClaims, {
          ...(rootUser || {}),
          ...(parentDoc || {}),
          role: "parent",
          schoolId: parentDoc?.schoolId || rootUser?.schoolId || effectiveSchoolId,
        });
      } else {
        resolvedProfile = buildSafeProfile(firebaseUser, nextClaims, {
          ...(rootUser || {}),
        });
      }

      setProfile(resolvedProfile);
    } catch (error) {
      console.error("resolveProfile failed:", error);

      setProfile(
        buildSafeProfile(firebaseUser, nextClaims, {
          role: nextClaims?.role || null,
        })
      );
    } finally {
      setProfileLoading(false);
      setProfileResolved(true);
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!mounted) return;

      setLoading(true);
      setAuthResolved(false);
      setProfileResolved(false);

      if (!firebaseUser) {
        clearAuthState();
        return;
      }

      try {
        const restored = readStoredMfaSession();

        setMfaSessionVerified(restored.verified);
        setMfaVerifiedAt(restored.verifiedAt);
        setMfaLastActivityAt(restored.lastActivityAt);

        const nextClaims = await readClaims(firebaseUser);

        if (!mounted) return;

        setUser(firebaseUser);
        setClaims(nextClaims || {});
        setAuthResolved(true);

        await resolveProfile(firebaseUser, nextClaims || {});
      } catch (error) {
        console.error("Auth bootstrap failed:", error);

        if (!mounted) return;

        setUser(firebaseUser);
        setClaims({});
        setProfile(buildSafeProfile(firebaseUser, {}, {}));
        setAuthResolved(true);
        setProfileResolved(true);
      } finally {
        if (mounted) setLoading(false);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [clearAuthState, resolveProfile]);

  useEffect(() => {
    if (!user?.uid || !mfaSessionVerified) return;

    const events = ["click", "keydown", "mousemove", "touchstart", "scroll"];

    const updateActivity = () => {
      const now = Date.now();
      localStorage.setItem("pp:mfaLastActivityAt", String(now));
      setMfaLastActivityAt(now);
    };

    events.forEach((event) => window.addEventListener(event, updateActivity));

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, updateActivity)
      );
    };
  }, [user?.uid, mfaSessionVerified]);

  useEffect(() => {
    if (!user?.uid || !mfaSessionVerified) return;

    const timer = setInterval(() => {
      const now = Date.now();

      const idleExpired =
        mfaLastActivityAt && now - mfaLastActivityAt > MFA_IDLE_LIMIT_MS;

      const absoluteExpired =
        mfaVerifiedAt && now - mfaVerifiedAt > MFA_ABSOLUTE_LIMIT_MS;

if (idleExpired || absoluteExpired) {
  clearMfaSessionStorage();
  clearStoredAdminSessionId();

  setMfaSessionVerified(false);
  setMfaVerifiedAt(null);
  setMfaLastActivityAt(Date.now());

  window.location.replace("/admin-mfa?reason=idle-timeout");
}
    }, 15000);

    return () => clearInterval(timer);
  }, [user?.uid, mfaSessionVerified, mfaVerifiedAt, mfaLastActivityAt]);

  useEffect(() => {
    if (
      !user?.uid ||
      !schoolId ||
      role !== "admin" ||
      !mfaSessionVerified
    ) {
      return;
    }

    let mounted = true;

    const bootSession = async () => {
      try {
        const existingSessionId = getStoredAdminSessionId();

        if (existingSessionId) {
          await updateAdminSessionHeartbeat({
            schoolId,
            sessionId: existingSessionId,
          });
        } else {
          await createAdminSession({
            uid: user.uid,
            schoolId,
            email: user.email || "",
            displayName: profile?.displayName || user.displayName || "",
            mfaVerified: true,
          });
        }
      } catch (error) {
        console.error("Admin session bootstrap failed:", error);
      }
    };

    bootSession();

    const heartbeat = setInterval(async () => {
      if (!mounted) return;

      try {
        const sessionId = getStoredAdminSessionId();

        if (!sessionId) return;

        await updateAdminSessionHeartbeat({
          schoolId,
          sessionId,
        });
      } catch (error) {
        console.error("Admin session heartbeat failed:", error);
      }
    }, 60000);

    return () => {
      mounted = false;
      clearInterval(heartbeat);
    };
  }, [user?.uid, user?.email, user?.displayName, schoolId, role, profile?.displayName, mfaSessionVerified]);

  useEffect(() => {
  if (
    !user?.uid ||
    !schoolId ||
    role !== "admin" ||
    !mfaSessionVerified
  ) {
    return;
  }

  let mounted = true;

  const checkRevokedSession = async () => {
    try {
      const sessionId = getStoredAdminSessionId();
      if (!sessionId) return;

      const session = await getAdminSession({ schoolId, sessionId });

      if (!mounted) return;

      if (!session || session.revoked === true) {
        clearMfaSessionStorage();
        clearStoredAdminSessionId();

        setMfaSessionVerified(false);
        setMfaVerifiedAt(null);
        setMfaLastActivityAt(Date.now());

        window.location.replace("/admin-mfa?session=revoked");
      }
    } catch (error) {
      console.error("Revoked session check failed:", error);
    }
  };

  checkRevokedSession();

  const timer = setInterval(checkRevokedSession, 15000);

  return () => {
    mounted = false;
    clearInterval(timer);
  };
}, [user?.uid, schoolId, role, mfaSessionVerified]);

  const refreshProfile = useCallback(async () => {
    if (!auth.currentUser) return;

    lastProfileKeyRef.current = "";

    const nextClaims = await readClaims(auth.currentUser);

    setClaims(nextClaims || {});
    await resolveProfile(auth.currentUser, nextClaims || {});
  }, [resolveProfile]);

  const markMfaSessionVerified = useCallback(async () => {
    const now = Date.now();

    storeMfaSession(now);

    setMfaSessionVerified(true);
    setMfaVerifiedAt(now);
    setMfaLastActivityAt(now);

    try {
      if (user?.uid && schoolId && role === "admin") {
        await createAdminSession({
          uid: user.uid,
          schoolId,
          email: user.email || "",
          displayName: profile?.displayName || user.displayName || "",
          mfaVerified: true,
        });
      }
    } catch (error) {
      console.error("Failed to create admin session:", error);
    }
  }, [user?.uid, user?.email, user?.displayName, schoolId, role, profile?.displayName]);

  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  }, []);

  const logout = useCallback(async () => {
    try {
      localStorage.removeItem("pp:lastChild");
      localStorage.removeItem("pp:biometricEnabled");
      clearMfaSessionStorage();
      clearStoredAdminSessionId();
      clearSensitiveActionVerification();

      await signOut(auth);
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      clearAuthState();
    }
  }, [clearAuthState]);

  const value = useMemo(() => {
    const mfaEnabled = !!profile?.mfaEnabled;
    const mfaCompleted = !!profile?.mfaCompleted;
    const mfaEnforced = !!profile?.mfaEnforced;
    const mfaMethod = profile?.mfaMethod || null;

    const requiresMfaSetup = role === "admin" && mfaEnforced && !mfaCompleted;

    const requiresMfaChallenge =
      role === "admin" && mfaEnforced && mfaCompleted && !mfaSessionVerified;

    return {
      user,
      firebaseUser: user,

      claims,
      profile,

      role,
      schoolId,

      portalName: PORTAL_NAME,

      mfaEnabled,
      mfaCompleted,
      mfaEnforced,
      mfaMethod,

      mfaSessionVerified,
      mfaVerifiedAt,
      mfaLastActivityAt,

      requiresMfaSetup,
      requiresMfaChallenge,

      markMfaSessionVerified,

      loading,
      profileLoading,
      authResolved,
      profileResolved,

      isReady: authResolved && profileResolved && !loading && !profileLoading,
      isAuthenticated: !!user,

      loginWithGoogle,
      logout,
      refreshProfile,
    };
  }, [
    user,
    claims,
    profile,
    role,
    schoolId,
    loading,
    profileLoading,
    authResolved,
    profileResolved,
    mfaSessionVerified,
    mfaVerifiedAt,
    mfaLastActivityAt,
    loginWithGoogle,
    logout,
    refreshProfile,
    markMfaSessionVerified,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return ctx;
}